import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/lib/api'
import { formatDate } from '@/lib/utils'
import { useAuthStore } from '@/stores/authStore'
import { UserPlus, Lock, Unlock, Pencil } from 'lucide-react'

interface User {
  id: string
  email: string
  full_name: string | null
  role: string
  is_active: boolean
  two_factor_enabled: boolean
  created_at: string | null
  last_login: string | null
}

export default function UserManagement() {
  const queryClient = useQueryClient()
  const currentUser = useAuthStore((s) => s.user)
  const [showInvite, setShowInvite] = useState(false)
  const [editUser, setEditUser] = useState<User | null>(null)
  const [inviteForm, setInviteForm] = useState({ email: '', full_name: '', role: 'User' })
  const [editForm, setEditForm] = useState({ full_name: '', role: 'User' })
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  const { data: users, isLoading } = useQuery<User[]>({
    queryKey: ['users'],
    queryFn: () => api.get('/users').then((res) => res.data),
  })

  const inviteMutation = useMutation({
    mutationFn: (data: { email: string; full_name: string; role: string }) =>
      api.post('/users', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      setShowInvite(false)
      setInviteForm({ email: '', full_name: '', role: 'User' })
      setMessage({ type: 'success', text: 'Access granted' })
    },
    onError: (err: any) => {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to grant access' })
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      api.put(`/users/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      setEditUser(null)
      setMessage({ type: 'success', text: 'User updated' })
    },
    onError: (err: any) => {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to update user' })
    },
  })

  const lockMutation = useMutation({
    mutationFn: ({ id, lock }: { id: string; lock: boolean }) =>
      api.put(`/users/${id}`, { is_active: !lock }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      setMessage({ type: 'success', text: 'Access updated' })
    },
  })

  const handleInvite = (e: React.FormEvent) => {
    e.preventDefault()
    setMessage(null)
    inviteMutation.mutate(inviteForm)
  }

  const handleEdit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!editUser) return
    setMessage(null)
    updateMutation.mutate({ id: editUser.id, data: editForm })
  }

  const openEdit = (user: User) => {
    setEditUser(user)
    setEditForm({ full_name: user.full_name || '', role: user.role })
  }

  if (isLoading) {
    return <div className="flex justify-center p-8"><span className="spinner" /></div>
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">User Management</h1>
          <p className="text-muted-foreground">Manage user accounts</p>
        </div>
        <button
          onClick={() => { setShowInvite(true); setMessage(null) }}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
        >
          <UserPlus className="h-4 w-4" />
          Grant Access
        </button>
      </div>

      {/* Status message */}
      {message && (
        <div className={`p-3 rounded-md text-sm ${
          message.type === 'success'
            ? 'bg-green-50 text-green-700 border border-green-200'
            : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          {message.text}
        </div>
      )}

      {/* Invite dialog */}
      {showInvite && (
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-semibold mb-4">Grant Access</h3>
          <p className="text-sm text-muted-foreground mb-4">
            No email is sent. This authorises a 42QLabs identity to use Plutus — the
            account itself is created in 42QLabs, and the two are matched by email the
            first time that person signs in.
          </p>
          <form onSubmit={handleInvite} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Email</label>
                <input
                  type="email"
                  required
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm((f) => ({ ...f, email: e.target.value }))}
                  className="w-full h-10 px-3 border border-input rounded-md bg-background text-sm"
                  placeholder="user@example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Full Name</label>
                <input
                  type="text"
                  value={inviteForm.full_name}
                  onChange={(e) => setInviteForm((f) => ({ ...f, full_name: e.target.value }))}
                  className="w-full h-10 px-3 border border-input rounded-md bg-background text-sm"
                  placeholder="John Doe"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Role</label>
                <select
                  value={inviteForm.role}
                  onChange={(e) => setInviteForm((f) => ({ ...f, role: e.target.value }))}
                  className="w-full h-10 px-3 border border-input rounded-md bg-background text-sm"
                >
                  <option value="User">Standard</option>
                  <option value="Admin">Admin</option>
                </select>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={inviteMutation.isPending}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm hover:bg-primary/90 disabled:opacity-50"
              >
                {inviteMutation.isPending ? 'Sending...' : 'Send Invite'}
              </button>
              <button
                type="button"
                onClick={() => setShowInvite(false)}
                className="px-4 py-2 border border-input rounded-md text-sm hover:bg-accent"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Edit dialog */}
      {editUser && (
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-semibold mb-4">Edit User: {editUser.email}</h3>
          <form onSubmit={handleEdit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Full Name</label>
                <input
                  type="text"
                  value={editForm.full_name}
                  onChange={(e) => setEditForm((f) => ({ ...f, full_name: e.target.value }))}
                  className="w-full h-10 px-3 border border-input rounded-md bg-background text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Role</label>
                <select
                  value={editForm.role}
                  onChange={(e) => setEditForm((f) => ({ ...f, role: e.target.value }))}
                  className="w-full h-10 px-3 border border-input rounded-md bg-background text-sm"
                >
                  <option value="User">Standard</option>
                  <option value="Admin">Admin</option>
                </select>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={updateMutation.isPending}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm hover:bg-primary/90 disabled:opacity-50"
              >
                {updateMutation.isPending ? 'Saving...' : 'Save'}
              </button>
              <button
                type="button"
                onClick={() => setEditUser(null)}
                className="px-4 py-2 border border-input rounded-md text-sm hover:bg-accent"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* User table */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <table className="data-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Name</th>
              <th>Role</th>
              <th>Status</th>
              <th>2FA</th>
              <th>Last Login</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users?.map((user) => (
              <tr key={user.id}>
                <td className="font-medium">{user.email}</td>
                <td>{user.full_name || '-'}</td>
                <td>
                  <span className={`px-2 py-1 rounded text-xs ${
                    user.role === 'Admin' ? 'bg-primary/10 text-primary' : 'bg-secondary'
                  }`}>
                    {user.role}
                  </span>
                </td>
                <td>
                  <span className={`px-2 py-1 rounded text-xs ${
                    user.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {user.is_active ? 'Active' : 'Locked'}
                  </span>
                </td>
                <td>
                  <span className={`px-2 py-1 rounded text-xs ${
                    user.two_factor_enabled ? 'bg-blue-100 text-blue-700' : 'bg-secondary text-muted-foreground'
                  }`}>
                    {user.two_factor_enabled ? 'Enabled' : 'Off'}
                  </span>
                </td>
                <td className="text-muted-foreground text-sm">{formatDate(user.last_login)}</td>
                <td>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => openEdit(user)}
                      title="Edit"
                      className="p-1.5 text-muted-foreground hover:text-primary rounded"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    {user.is_active ? (
                      <button
                        onClick={() => lockMutation.mutate({ id: user.id, lock: true })}
                        title="Revoke access"
                        disabled={user.id === currentUser?.id}
                        className="p-1.5 text-muted-foreground hover:text-orange-600 rounded disabled:opacity-30"
                      >
                        <Lock className="h-4 w-4" />
                      </button>
                    ) : (
                      <button
                        onClick={() => lockMutation.mutate({ id: user.id, lock: false })}
                        title="Restore access"
                        className="p-1.5 text-muted-foreground hover:text-green-600 rounded"
                      >
                        <Unlock className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
